import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '@/utils/api';
import useAppStore from '@/store/appStore';

/**
 * Hook for OAuth2 authentication flow
 */
export function useOAuth2() {
  const [loading, setLoading] = useState(false);
  const { addNotification } = useAppStore();

  // Fetch available OAuth2 providers
  const { data: providersData } = useQuery({
    queryKey: ['oauth2', 'providers'],
    queryFn: async () => {
      // Note: api interceptor already returns response.data
      const data = await api.get('/oauth2/providers', {
        headers: { 'Cache-Control': 'no-cache' }
      });
      return data;
    },
    staleTime: 0, // Always consider stale to allow immediate refetch
  });

  const availableProviders = providersData?.providers || [];
  const providerDetails = providersData?.details || {};

  /**
   * Start OAuth2 authorization flow
   * Opens popup window and waits for callback
   */
  const startOAuth2Flow = async (provider) => {
    setLoading(true);

    try {
      // Get authorization URL from backend
      const { data } = await api.post('/oauth2/authorize', { provider });
      const { authUrl, state } = data;

      // Open OAuth2 popup
      const popup = window.open(
        authUrl,
        'OAuth2 Authorization',
        'width=600,height=700,left=200,top=100,resizable=yes,scrollbars=yes'
      );

      if (!popup) {
        throw new Error('Failed to open popup. Please allow popups for this site.');
      }

      // Wait for OAuth2 callback
      return new Promise((resolve, reject) => {
        // Listen for postMessage from callback page
        const handleMessage = (event) => {
          // Verify message is from our callback
          if (event.data && event.data.type === 'oauth2-callback') {
            window.removeEventListener('message', handleMessage);
            clearInterval(checkClosed);

            if (event.data.success) {
              addNotification({
                type: 'success',
                title: 'OAuth2 Authentication Successful',
                message: `Successfully authenticated with ${provider}`,
              });
              resolve(event.data.tokens);
            } else {
              addNotification({
                type: 'error',
                title: 'OAuth2 Authentication Failed',
                message: event.data.error || 'Unknown error occurred',
              });
              reject(new Error(event.data.error || 'OAuth2 authorization failed'));
            }
          }
        };

        window.addEventListener('message', handleMessage);

        // Check if popup was closed manually
        const checkClosed = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(checkClosed);
              window.removeEventListener('message', handleMessage);
              addNotification({
                type: 'error',
                title: 'OAuth2 Cancelled',
                message: 'Authorization window was closed',
              });
              reject(new Error('Authorization window closed'));
            }
          } catch {
            // Cross-origin popup access blocked by browser policy
          }
        }, 500);
      });
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'OAuth2 Flow Failed',
        message: err.message || 'Failed to start OAuth2 flow',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get OAuth2 status for an account
   */
  const getAccountStatus = async (accountId) => {
    const response = await api.get(`/oauth2/status/${accountId}`);
    return response.data;
  };

  /**
   * Manually refresh OAuth2 token for an account
   */
  const refreshToken = useMutation({
    mutationFn: async (accountId) => {
      const response = await api.post(`/oauth2/refresh/${accountId}`);
      return response.data;
    },
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Token Refreshed',
        message: 'OAuth2 token refreshed successfully',
      });
    },
    onError: (err) => {
      addNotification({
        type: 'error',
        title: 'Token Refresh Failed',
        message: err.message,
      });
    },
  });

  /**
   * Revoke OAuth2 tokens for an account
   */
  const revokeTokens = useMutation({
    mutationFn: async (accountId) => {
      const response = await api.post(`/oauth2/revoke/${accountId}`);
      return response.data;
    },
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Tokens Revoked',
        message: 'OAuth2 tokens revoked successfully',
      });
    },
    onError: (err) => {
      addNotification({
        type: 'error',
        title: 'Revoke Failed',
        message: err.message,
      });
    },
  });

  return {
    availableProviders,
    providerDetails,
    startOAuth2Flow,
    getAccountStatus,
    refreshToken: refreshToken.mutateAsync,
    revokeTokens: revokeTokens.mutateAsync,
    loading,
  };
}

/**
 * Detect OAuth2 provider from email domain
 */
export function detectOAuth2Provider(email) {
  if (!email || !email.includes('@')) return null;

  const domain = email.split('@')[1].toLowerCase();

  // Google domains
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    return 'google';
  }

  // Microsoft domains
  if (
    domain === 'outlook.com' ||
    domain === 'hotmail.com' ||
    domain === 'live.com' ||
    domain === 'msn.com'
  ) {
    return 'microsoft';
  }

  // Office 365 domains typically use custom domains, so can't reliably detect
  return null;
}

/**
 * Get app password setup URL for provider
 */
export function getAppPasswordUrl(provider) {
  switch (provider) {
    case 'google':
      return 'https://myaccount.google.com/apppasswords';
    case 'microsoft':
      return 'https://account.live.com/proofs/AppPassword';
    default:
      return null;
  }
}

/**
 * Get provider-specific help text for basic auth
 */
export function getBasicAuthHelpText(provider) {
  switch (provider) {
    case 'google':
      return {
        title: 'Gmail requires App Password',
        text: 'Gmail requires 2-Factor Authentication and an App Password. Your regular password will not work.',
        link: 'https://myaccount.google.com/apppasswords',
        linkText: 'Generate App Password',
      };
    case 'microsoft':
      return {
        title: 'Outlook/Hotmail Note',
        text: 'OAuth2 is recommended for Microsoft accounts, especially on work VPNs. Basic auth may be restricted.',
        link: null,
        linkText: null,
      };
    default:
      return null;
  }
}
